import React, { useEffect, useState, useCallback } from 'react';
import {
  withTranslation,
  withUserPreferences,
  withCustomRouter,
  withTaskMonitor,
} from 'components/hoc';
import LottieLoader from 'components/common/LottieLoader';
// import ContentTemplate from 'components/common/ContentTemplate';
// import AdminContentTemplate from 'components/Admin/common/AdminContentTemplate';
import compose from 'lodash/fp/compose';

const TaskList = (props) => {  
  const { 
    loaded, 
    taskMonitor, 
    tr,
    loading,
    error,
    refreshTaskMonitor
  } = props;

  const [manualRefreshing, setManualRefreshing] = useState(false);

  useEffect(() => {
    console.log('TaskList useEffect - Initial load check', {
      loaded,
      loading,
      taskMonitorDataLength: taskMonitor?.taskMonitorData?.length
    });
  }, [loaded, loading, taskMonitor?.taskMonitorData?.length]);

  const handleRefresh = useCallback(() => {
    if (manualRefreshing) return;
    setManualRefreshing(true);
    if (refreshTaskMonitor) {
      refreshTaskMonitor()
        .then(() => {
          console.log('Refresh successful');
        })
        .catch(err => {
          console.error('Refresh failed:', err);
        })
        .finally(() => {
          setTimeout(() => {
            setManualRefreshing(false);
          }, 1000);
        });
    } else {
      setTimeout(() => {
        setManualRefreshing(false);
      }, 1000);
    }
  }, [refreshTaskMonitor, manualRefreshing]);

  const getStatusColor = useCallback((status) => {
    const statusColors = {
      'SUCCESS': '#28a745',
      'FAILURE': '#dc3545', 
      'PENDING': '#ffc107',
      'STARTED': '#17a2b8'
    };
    return statusColors[status] || '#6c757d';
  }, []);

  const mapFields = useCallback(() => {
    const tasks = taskMonitor?.taskMonitorData || [];
    
    if (!Array.isArray(tasks)) {
      console.warn("Tasks data is not an array:", tasks);
      return [];
    }

    if (tasks.length > 0) {
      console.log("First task sample:", tasks[0]);
      console.log("First task keys:", Object.keys(tasks[0]));
    }

    // Create clean, serializable data
    const mappedTasks = tasks.map((task, index) => {
      const mappedTask = {
        id: task.id || index,
        task_id_short: task.task_id ? task.task_id.substring(0, 8) + '...' : 'N/A',
        task_name: task.task_name || 'N/A',
        status: task.status || 'UNKNOWN',
        start_time_formatted: task.start_time ? new Date(task.start_time).toLocaleString() : 'N/A',
        end_time_formatted: task.end_time ? new Date(task.end_time).toLocaleString() : 'N/A',
        duration: task.runtime ? `${parseFloat(task.runtime).toFixed(2)}s` : 'N/A',
        error_message: task.error_message || '',
        statusColor: getStatusColor(task.status)
      };
      return mappedTask;
    });
    
    return mappedTasks;
  }, [taskMonitor?.taskMonitorData, getStatusColor]);

  const mappedData = mapFields();

  const columns = [
    { 
      key: 'task_id_short', 
      label: tr('Task ID'),
      width: '150px'
    },
    { 
      key: 'task_name', 
      label: tr('Task Name'),
      width: '250px'
    },
    { 
      key: 'status', 
      label: tr('Status'),
      width: '100px',
      render: (value, row) => (
        <span
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            color: 'white',
            backgroundColor: row.statusColor,
            fontSize: '12px'
          }}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'start_time_formatted', 
      label: tr('Start Time'),
      width: '180px'
    },
    { 
      key: 'end_time_formatted', 
      label: tr('End Time'),
      width: '180px'
    },
    { 
      key: 'duration', 
      label: tr('Duration'),
      width: '120px'
    },
    { 
      key: 'error_message', 
      label: tr('Error Message'),
      width: '300px'
    },
  ];

  // Show loading state
  if (loading || manualRefreshing) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>{tr('Task Monitor')}</h2>
        <LottieLoader />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <h2>{tr('Task Monitor')}</h2>
        <div style={{ color: 'red', padding: '20px' }}>
          Error loading task data: {error.toString()}
          <button 
            onClick={handleRefresh}
            style={{ marginLeft: '10px' }}
            disabled={manualRefreshing}
          >
            {manualRefreshing ? 'Refreshing...' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: 'var(--admincat-color-grey-3)' }}>{tr('Task Monitor')}</h2>
        <div>
          <button 
            onClick={handleRefresh}
            disabled={manualRefreshing}
            style={{ 
              padding: '8px 16px', 
              marginRight: '10px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: manualRefreshing ? 'not-allowed' : 'pointer',
              opacity: manualRefreshing ? 0.6 : 1
            }}
          >
            {manualRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <span style={{ color: '#666', fontSize: '14px' }}>
            {mappedData.length} tasks found
          </span>
        </div>
      </div>

      {/* <AdminContentTemplate
        data={mappedData}
        columns={columns}
        showDetailOnRowClick={false}
        hasBulkDelete={false}
      /> */}

{!loaded ? (
  <div style={{ 
    padding: '40px', 
    textAlign: 'center', 
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  }}>
    <h4>{tr('Loading...')}</h4>
    <p>{tr('Task monitoring data is being loaded.')}</p>
  </div>
) : mappedData.length === 0 ? (
  <div style={{ 
    padding: '40px', 
    textAlign: 'center', 
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #dee2e6'
  }}>
    <h4>{tr('No tasks found')}</h4>
    <p>{tr('No task monitoring data is available. Tasks will appear here when they are executed.')}</p>
  </div>
) : (
  <div className="card">
    <div className="table-responsive" style={{ 
      height: 'auto',
      minHeight: '500px',
      maxHeight: '700px',
      background: 'var(--admincat-color-grey-1, #f8f9fa)'
    }}>
      <table className="table table-striped" style={{ width: '100%' }}>
        <thead>
          <tr>
            {columns.map(column => (
              <th 
                key={column.key} 
                style={{ 
                  width: column.width,
                  height: '36px',
                  lineHeight: '40px',
                  padding: '0 1rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  background: 'var(--admincat-color-grey-1, #fff)',
                  position: 'sticky',
                  top: '0px',
                  zIndex: 200,
                  borderBottom: '2px solid #dee2e6',
                  fontWeight: '600'
                }}
              >
                <p style={{ 
                  fontSize: '1rem',
                  display: 'inline-block',
                  transform: 'translateY(1px)',
                  color: 'var(--admincat-color-grey-3)',
                  margin: 0
                }}>
                  {column.label}
                </p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mappedData.map((row, index) => (
            <tr 
              key={row.id || index}
              style={{
                background: index % 2 === 0 ? '#fff' : '#f8f9fa',
                transition: 'background 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e3f2fd';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#f8f9fa';
              }}
            >
              {columns.map(column => (
                <td 
                  key={column.key}
                  style={{
                    height: '50px',
                    lineHeight: '50px',
                    padding: '0 1rem',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid #dee2e6',
                    verticalAlign: 'middle',
                    background: 'var(--admincat-color-grey-1)',
                  }}
                >
                  {column.render ? 
                    column.render(row[column.key], row) : 
                    <span style={{ color: 'var(--admincat-color-grey-2)' }}>
                      {row[column.key] || 'N/A'}
                    </span>
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

    </div>
  );
};

export default compose(
  withTranslation,
  withCustomRouter,
  withUserPreferences,
  withTaskMonitor,
)(TaskList);